import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  bulkUpdatePackages,
  createPackage,
  deletePackage,
  getPackage,
  listDestinations,
  listPackages,
  listThemes,
  updatePackage,
  uploadAdminImages,
} from '../../api/adminPackages';
import { useToast } from '../../components/ToastContext';
import Button from '../../components/shared/ui/Button';
import { cx } from '../../components/shared/ui/utils';

const inputClassName =
  'mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-100';
const textLabelClass = 'text-xs font-semibold uppercase tracking-[0.22em] text-slate-500';

const emptyForm = () => ({
  id: null,
  title: '',
  slug: '',
  shortTitle: '',
  subtitle: '',
  tagline: '',
  destinationId: '',
  shortDescription: '',
  overview: '',
  description: '',
  categories: '',
  highlights: '',
  inclusions: '',
  exclusions: '',
  durationDays: '',
  durationNights: '',
  priceFrom: '',
  originalPrice: '',
  currency: 'INR',
  rating: '4.8',
  reviewCount: '',
  minPersons: '',
  maxPersons: '',
  maxGuests: '',
  groupSize: '',
  difficultyLevel: '',
  availabilityType: 'flexible',
  availabilityStart: '',
  availabilityEnd: '',
  seasonalText: '',
  themes: [],
  images: [],
  heroImage: '',
  thumbnailImage: '',
  cardImage: '',
  itinerary: [],
  badgeFeatured: false,
  badgeTrending: false,
  badgeTopRated: false,
  isFeaturedHome: false,
  isFeaturedListing: true,
  isActive: true,
  isDraft: false,
  status: 'published',
  sortOrder: '0',
  requestOnly: false,
  enquireButtonLabel: 'Enquire now',
  detailCtaLabel: 'View details',
  seoTitle: '',
  seoDescription: '',
  seoKeywords: '',
  customSections: '{}',
  previewUrl: '',
});

const slugifyText = (value) =>
  (value || '')
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const getPublicOrigin = () =>
  typeof window !== 'undefined' && window.location?.origin
    ? window.location.origin.replace(/\/+$/, '')
    : 'https://www.homevilas.com';

const copyTextToClipboard = async (text) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
};

const buildPackagePublicUrl = (item = {}) => {
  const slug = String(item.slug || '').trim() || slugifyText(item.title || item.shortTitle || item.short_title || '');
  if (!slug) return '';
  return `${getPublicOrigin()}/packages/${encodeURIComponent(slug)}`;
};

const toLines = (value) =>
  String(value || '')
    .split('\n')
    .map((item) => item.trim())
    .filter(Boolean);

const fromList = (value) => (Array.isArray(value) ? value.join('\n') : '');
const safeParseCustomSections = (value) => {
  if (!value || !String(value).trim()) return {};
  try {
    const parsed = JSON.parse(value);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return parsed;
  } catch (_) {
    return null;
  }
};
const stable = (value) => JSON.stringify(value);

const mapImage = (item, index = 0) => ({
  imageUrl: item?.image_url || item?.imageUrl || '',
  altText: item?.alt_text || item?.altText || '',
  imageType: item?.image_type || item?.imageType || 'gallery',
  isCover: Boolean(item?.is_cover || item?.isCover || index === 0),
  isPrimary: Boolean(item?.is_primary || item?.isPrimary || index === 0),
  sortOrder: item?.sort_order ?? item?.sortOrder ?? index,
});

const mapItinerary = (item, index = 0) => ({
  dayNumber: item?.day_number ?? item?.dayNumber ?? index + 1,
  dayTitle: item?.day_title || item?.dayTitle || '',
  dayDescription: item?.day_description || item?.description || '',
  stayInfo: item?.stay_info || item?.stayInfo || '',
  mealsInfo: item?.meals_info || item?.mealsInfo || '',
  imageUrl: item?.image_url || item?.imageUrl || '',
  sortOrder: item?.sort_order ?? item?.sortOrder ?? index,
});

const mapPackageToForm = (data) => ({
  id: data.id,
  title: data.title || '',
  slug: data.slug || '',
  shortTitle: data.short_title || '',
  subtitle: data.subtitle || '',
  tagline: data.tagline || '',
  destinationId: data.destination_id || '',
  shortDescription: data.short_description || '',
  overview: data.overview || '',
  description: data.description || '',
  categories: fromList(data.categories || []),
  highlights: fromList(data.highlights || []),
  inclusions: fromList(data.inclusions || []),
  exclusions: fromList(data.exclusions || []),
  durationDays: data.duration_days ?? '',
  durationNights: data.duration_nights ?? '',
  priceFrom: data.price_from ?? data.price_per_person ?? '',
  originalPrice: data.original_price ?? '',
  currency: data.currency || 'INR',
  rating: data.rating ?? '4.8',
  reviewCount: data.review_count ?? '',
  minPersons: data.min_persons ?? '',
  maxPersons: data.max_persons ?? '',
  maxGuests: data.max_guests ?? '',
  groupSize: data.group_size ?? '',
  difficultyLevel: data.difficulty_level || '',
  availabilityType: data.availability_type || 'flexible',
  availabilityStart: data.availability_start_date || '',
  availabilityEnd: data.availability_end_date || '',
  seasonalText: data.seasonal_text || '',
  themes: (data.themes || []).map((item) => item.id),
  images: (data.images || []).map(mapImage),
  heroImage: data.hero_image || '',
  thumbnailImage: data.thumbnail_image || '',
  cardImage: data.card_image || '',
  itinerary: (data.itinerary || []).map(mapItinerary),
  badgeFeatured: Boolean(data.badge_featured ?? data.is_featured),
  badgeTrending: Boolean(data.badge_trending ?? data.is_trending),
  badgeTopRated: Boolean(data.badge_top_rated ?? data.is_top_rated),
  isFeaturedHome: Boolean(data.is_featured_home),
  isFeaturedListing: data.is_featured_listing !== false,
  isActive: data.is_active !== false,
  isDraft: Boolean(data.is_draft),
  status: data.status || 'published',
  sortOrder: data.sort_order ?? '0',
  requestOnly: Boolean(data.request_only),
  enquireButtonLabel: data.enquire_button_label || 'Enquire now',
  detailCtaLabel: data.detail_cta_label || 'View details',
  seoTitle: data.seo_title || '',
  seoDescription: data.seo_description || '',
  seoKeywords: data.seo_keywords || '',
  customSections: JSON.stringify(data.custom_sections || {}, null, 2),
  previewUrl: data.preview_url || (data.slug ? `/packages/${data.slug}` : ''),
});

const validate = (form) => {
  const next = {};
  if (!form.title.trim()) next.title = 'Title is required.';
  if (!form.destinationId) next.destinationId = 'Select a destination.';
  if (!form.shortDescription.trim()) next.shortDescription = 'Short description is required.';
  if (!form.durationDays) next.durationDays = 'Days are required.';
  if (!form.durationNights) next.durationNights = 'Nights are required.';
  if (!form.priceFrom) next.priceFrom = 'Price is required.';
  if (!form.enquireButtonLabel.trim()) next.enquireButtonLabel = 'CTA is required.';
  if (!form.detailCtaLabel.trim()) next.detailCtaLabel = 'CTA is required.';
  if (form.customSections.trim()) {
    const parsed = safeParseCustomSections(form.customSections);
    if (!parsed) next.customSections = 'Custom sections must be valid JSON object.';
  }
  return next;
};

const buildPayload = (form) => ({
  title: form.title,
  slug: form.slug || undefined,
  short_title: form.shortTitle || null,
  subtitle: form.subtitle || null,
  tagline: form.tagline || null,
  destination_id: form.destinationId || null,
  short_description: form.shortDescription,
  overview: form.overview,
  description: form.description,
  categories: toLines(form.categories),
  highlights: toLines(form.highlights),
  inclusions: toLines(form.inclusions),
  exclusions: toLines(form.exclusions),
  duration_days: form.durationDays || null,
  duration_nights: form.durationNights || null,
  price_from: form.priceFrom || null,
  original_price: form.originalPrice || null,
  currency: form.currency || 'INR',
  rating: form.rating || null,
  review_count: form.reviewCount || null,
  min_persons: form.minPersons || null,
  max_persons: form.maxPersons || null,
  max_guests: form.maxGuests || null,
  group_size: form.groupSize || null,
  difficulty_level: form.difficultyLevel || null,
  availability_type: form.availabilityType,
  availability_start: form.availabilityStart || null,
  availability_end: form.availabilityEnd || null,
  seasonal_text: form.seasonalText || null,
  themes: form.themes,
  gallery_images: form.images.map((image, index) => ({
    image_url: image.imageUrl,
    alt_text: image.altText || '',
    image_type: image.imageType || 'gallery',
    is_cover: image.isCover || index === 0,
    is_primary: image.isPrimary || index === 0,
    sort_order: image.sortOrder ?? index,
  })),
  hero_image: form.heroImage || null,
  thumbnail_image: form.thumbnailImage || null,
  card_image: form.cardImage || null,
  itinerary_days: form.itinerary.map((item, index) => ({
    day_number: item.dayNumber || index + 1,
    day_title: item.dayTitle || `Day ${index + 1}`,
    day_description: item.dayDescription || '',
    stay_info: item.stayInfo || '',
    meals_info: item.mealsInfo || '',
    image_url: item.imageUrl || '',
    sort_order: item.sortOrder ?? index,
  })),
  badge_featured: form.badgeFeatured,
  badge_trending: form.badgeTrending,
  badge_top_rated: form.badgeTopRated,
  is_featured: form.badgeFeatured,
  is_trending: form.badgeTrending,
  is_top_rated: form.badgeTopRated,
  is_featured_home: form.isFeaturedHome,
  is_featured_listing: form.isFeaturedListing,
  is_active: form.isActive,
  is_draft: form.isDraft,
  status: form.status,
  sort_order: form.sortOrder || 0,
  request_only: form.requestOnly,
  enquire_button_label: form.enquireButtonLabel,
  detail_cta_label: form.detailCtaLabel,
  seo_title: form.seoTitle || null,
  seo_description: form.seoDescription || null,
  seo_keywords: form.seoKeywords || null,
  custom_sections: safeParseCustomSections(form.customSections) || {},
});

const ErrorText = ({ value }) =>
  value ? <p className="mt-1 text-xs font-medium text-rose-600">{value}</p> : null;

const getStatusTone = (item) => {
  const status = (item?.status || '').toLowerCase();
  const isInactive = item?.is_active === false || item?.active === false;
  if (status === 'published' && !isInactive) return 'bg-emerald-50 text-emerald-700';
  if (status === 'draft' || item?.is_draft) return 'bg-amber-50 text-amber-700';
  if (isInactive || status === 'unpublished' || status === 'archived') {
    return 'bg-slate-100 text-slate-600';
  }
  return 'bg-sky-50 text-sky-700';
};

const buildStatusChips = (item) => {
  const chips = [];
  chips.push(item?.status || 'draft');
  if (item?.is_active === false || item?.active === false) chips.push('inactive');
  if (item?.badge_featured || item?.is_featured) chips.push('featured');
  if (item?.badge_trending || item?.is_trending) chips.push('trending');
  if (item?.badge_top_rated || item?.is_top_rated) chips.push('top rated');
  return [...new Set(chips)];
};

const AdminPackages = () => {
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [libraryView, setLibraryView] = useState('list');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkAction, setBulkAction] = useState('publish');
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const baselineRef = useRef(stable(emptyForm()));

  const loadAll = async () => {
    setLoading(true);
    setError('');
    try {
      const [packagesRes, destinationsRes, themesRes] = await Promise.all([
        listPackages({ pageSize: 200 }),
        listDestinations(),
        listThemes(),
      ]);
      setItems(packagesRes?.data || []);
      setDestinations(destinationsRes?.data || []);
      setThemes(themesRes?.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load packages.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    const packageId = searchParams.get('packageId');
    if (!packageId || String(form.id || '') === String(packageId)) return;
    getPackage(packageId)
      .then((res) => {
        const next = mapPackageToForm(res?.data || res || {});
        setForm(next);
        baselineRef.current = stable(next);
        setErrors({});
      })
      .catch((err) => setError(err.message || 'Unable to load package.'));
  }, [searchParams, form.id]);

  const dirty = useMemo(() => stable(form) !== baselineRef.current, [form]);
  const selectedDestination = useMemo(
    () => destinations.find((item) => String(item.id) === String(form.destinationId)),
    [destinations, form.destinationId]
  );

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return items;
    return items.filter((item) =>
      [item.title, item.slug, item.destination_name, item.package_code]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [items, search]);

  const visibleIds = useMemo(
    () => filteredItems.map((item) => Number(item.id)).filter(Number.isFinite),
    [filteredItems]
  );
  const allVisibleSelected = useMemo(
    () => visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id)),
    [selectedIds, visibleIds]
  );

  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  useEffect(() => {
    setSelectedIds((prev) => prev.filter((id) => items.some((item) => Number(item.id) === Number(id))));
  }, [items]);

  const updateField = (key, value) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'title' && !prev.slug) next.slug = slugifyText(value);
      if (key === 'status') {
        next.isDraft = value === 'draft';
        next.isActive = value !== 'unpublished';
      }
      return next;
    });
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const resetForm = () => {
    if (dirty && !window.confirm('Discard unsaved changes?')) return;
    const next = emptyForm();
    setForm(next);
    baselineRef.current = stable(next);
    setErrors({});
    setError('');
  };

  const openRecord = async (id) => {
    if (dirty && !window.confirm('Open another package and discard unsaved changes?')) return;
    try {
      const res = await getPackage(id);
      const next = mapPackageToForm(res?.data || res || {});
      setForm(next);
      baselineRef.current = stable(next);
      setErrors({});
    } catch (err) {
      setError(err.message || 'Unable to open package.');
    }
  };

  const save = async () => {
    const nextErrors = validate(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      toast.error('Fix the highlighted fields and save again.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = buildPayload(form);
      const res = form.id ? await updatePackage(form.id, payload) : await createPackage(payload);
      const next = mapPackageToForm(res?.data || res || {});
      setForm(next);
      baselineRef.current = stable(next);
      await loadAll();
      toast.success('Package saved.');
    } catch (err) {
      setError(err.message || 'Unable to save package.');
      toast.error(err.message || 'Unable to save package.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!form.id || !window.confirm('Delete this package permanently?')) return;
    setSaving(true);
    try {
      await deletePackage(form.id);
      const next = emptyForm();
      setForm(next);
      baselineRef.current = stable(next);
      setErrors({});
      await loadAll();
      toast.success('Package deleted.');
    } catch (err) {
      setError(err.message || 'Delete failed.');
      toast.error(err.message || 'Delete failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    try {
      const res = await uploadAdminImages(files);
      const paths = res?.paths || [];
      setForm((prev) => {
        const images = [
          ...prev.images,
          ...paths.map((path, index) => mapImage({ image_url: path }, prev.images.length + index)),
        ];
        return {
          ...prev,
          images,
          heroImage: prev.heroImage || paths[0] || '',
          thumbnailImage: prev.thumbnailImage || paths[0] || '',
          cardImage: prev.cardImage || paths[0] || '',
        };
      });
      toast.success('Images uploaded.');
    } catch (err) {
      setError(err.message || 'Upload failed.');
      toast.error(err.message || 'Upload failed.');
    } finally {
      event.target.value = '';
    }
  };

  const toggleTheme = (themeId) => {
    setForm((prev) => ({
      ...prev,
      themes: prev.themes.includes(themeId)
        ? prev.themes.filter((id) => id !== themeId)
        : [...prev.themes, themeId],
    }));
  };

  const setImageRole = (key, imageUrl) => updateField(key, imageUrl);
  const updateImage = (index, patch) =>
    setForm((prev) => ({
      ...prev,
      images: prev.images.map((image, i) => (i === index ? { ...image, ...patch } : image)),
    }));
  const removeImage = (index) =>
    setForm((prev) => {
      const removed = prev.images[index];
      return {
        ...prev,
        images: prev.images.filter((_, i) => i !== index).map((image, i) => ({ ...image, sortOrder: i })),
        heroImage: prev.heroImage === removed?.imageUrl ? '' : prev.heroImage,
        thumbnailImage: prev.thumbnailImage === removed?.imageUrl ? '' : prev.thumbnailImage,
        cardImage: prev.cardImage === removed?.imageUrl ? '' : prev.cardImage,
      };
    });

  const addDay = () =>
    setForm((prev) => ({
      ...prev,
      itinerary: [...prev.itinerary, mapItinerary({ day_number: prev.itinerary.length + 1 }, prev.itinerary.length)],
    }));
  const updateDay = (index, key, value) =>
    setForm((prev) => ({
      ...prev,
      itinerary: prev.itinerary.map((item, i) => (i === index ? { ...item, [key]: value } : item)),
    }));
  const removeDay = (index) =>
    setForm((prev) => ({
      ...prev,
      itinerary: prev.itinerary
        .filter((_, i) => i !== index)
        .map((item, i) => ({ ...item, dayNumber: i + 1, sortOrder: i })),
    }));

  const toggleSelect = (id) => {
    const nextId = Number(id);
    setSelectedIds((prev) =>
      prev.includes(nextId) ? prev.filter((itemId) => itemId !== nextId) : [...prev, nextId]
    );
  };

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
      return;
    }
    setSelectedIds((prev) => [...new Set([...prev, ...visibleIds])]);
  };

  const refreshCurrentRecord = async (ids = []) => {
    if (!form.id) return;
    if (!ids.includes(Number(form.id))) return;
    if (dirty) return;
    try {
      const res = await getPackage(form.id);
      const next = mapPackageToForm(res?.data || res || {});
      setForm(next);
      baselineRef.current = stable(next);
    } catch (_) {
      // ignore refresh errors; list reload already completed
    }
  };

  const runBulkAction = async () => {
    if (!selectedIds.length) {
      toast.error('Select at least one package.');
      return;
    }

    const actionLabelMap = {
      publish: 'publish',
      unpublish: 'unpublish',
      activate: 'activate',
      deactivate: 'deactivate',
      mark_featured: 'mark as featured',
    };
    const actionLabel = actionLabelMap[bulkAction] || bulkAction;
    if (!window.confirm(`Apply "${actionLabel}" to ${selectedIds.length} selected package(s)?`)) return;

    setBulkBusy(true);
    setError('');
    try {
      await bulkUpdatePackages({ ids: selectedIds, action: bulkAction });
      await loadAll();
      await refreshCurrentRecord(selectedIds);
      setSelectedIds([]);
      toast.success(`Bulk action applied to ${selectedIds.length} package(s).`);
    } catch (err) {
      setError(err.message || 'Bulk update failed.');
      toast.error(err.message || 'Bulk update failed.');
    } finally {
      setBulkBusy(false);
    }
  };

  const previewDetail = () => {
    const href = form.previewUrl || `/packages/${form.slug || slugifyText(form.title)}`;
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  const previewListing = () => {
    const params = new URLSearchParams();
    const queryText = form.title || form.shortTitle || form.slug;
    const destinationName =
      destinations.find((item) => String(item.id) === String(form.destinationId))?.slug ||
      destinations.find((item) => String(item.id) === String(form.destinationId))?.name;
    if (queryText) params.set('search', queryText);
    if (destinationName) params.set('destination', destinationName);
    const href = `/packages/list${params.toString() ? `?${params.toString()}` : ''}`;
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  const copyPackageUrl = async (item = form) => {
    const url = buildPackagePublicUrl(item);
    if (!url) {
      toast.error('Package slug is required to copy URL.');
      return;
    }
    try {
      await copyTextToClipboard(url);
      toast.success(`Copied package URL: ${url}`);
    } catch (err) {
      toast.error(err.message || 'Failed to copy package URL.');
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-400">Tours & Packages</p>
          <h1 className="mt-2 text-2xl font-semibold text-slate-950">Travel Packages</h1>
          <p className="mt-1 text-sm text-slate-500">
            Manage dynamic packages for Home, listing, detail, and enquiry handoff.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {form.id ? (
            <>
              <Button variant="outline" onClick={() => copyPackageUrl()}>
                Copy URL
              </Button>
              <Button variant="outline" onClick={previewListing}>
                Preview listing card
              </Button>
              <Button variant="outline" onClick={previewDetail}>
                Preview detail page
              </Button>
            </>
          ) : null}
          <Button variant="outline" onClick={resetForm}>
            New package
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving ? 'Saving...' : 'Save package'}
          </Button>
        </div>
      </div>

      {error ? (
        <div className="rounded-3xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
      ) : null}

      <div className="grid gap-6 xl:grid-cols-[360px_minmax(0,1fr)]">
        <aside className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">Library</p>
              <h2 className="mt-1 text-lg font-semibold text-slate-950">Package records</h2>
            </div>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-600">
              {dirty ? 'Unsaved' : 'Synced'}
            </span>
          </div>
          <div className="mt-4 space-y-3">
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search packages"
              className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none transition focus:border-teal-500 focus:bg-white focus:ring-2 focus:ring-teal-100"
            />
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="inline-flex rounded-full border border-slate-200 bg-slate-50 p-1">
                {['list', 'table'].map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setLibraryView(mode)}
                    className={cx(
                      'rounded-full px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] transition',
                      libraryView === mode ? 'bg-slate-900 text-white' : 'text-slate-500'
                    )}
                  >
                    {mode}
                  </button>
                ))}
              </div>
              <label className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleSelectAllVisible}
                />
                Select visible
              </label>
            </div>
            <div className="grid gap-2">
              <select
                value={bulkAction}
                onChange={(event) => setBulkAction(event.target.value)}
                className="rounded-2xl border border-slate-200 px-3 py-3 text-sm"
              >
                <option value="publish">Publish selected</option>
                <option value="unpublish">Unpublish selected</option>
                <option value="activate">Activate selected</option>
                <option value="deactivate">Deactivate selected</option>
                <option value="mark_featured">Mark featured</option>
              </select>
              <button
                type="button"
                onClick={runBulkAction}
                disabled={bulkBusy || !selectedIds.length}
                className="rounded-full bg-slate-900 px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                {bulkBusy ? 'Applying...' : `Run bulk action${selectedIds.length ? ` (${selectedIds.length})` : ''}`}
              </button>
            </div>
          </div>
          <div className="mt-4 max-h-[820px] overflow-y-auto pr-1">
            {loading ? (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                Loading packages...
              </div>
            ) : filteredItems.length ? (
              libraryView === 'table' ? (
                <div className="overflow-hidden rounded-[24px] border border-slate-200">
                  <table className="min-w-full text-left text-sm">
                    <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                      <tr>
                        <th className="px-3 py-3" />
                        <th className="px-3 py-3">Title</th>
                        <th className="px-3 py-3">Destination</th>
                        <th className="px-3 py-3">Price</th>
                        <th className="px-3 py-3">Status</th>
                        <th className="px-3 py-3">Badges</th>
                        <th className="px-3 py-3">URL</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {filteredItems.map((item) => {
                        const active = String(form.id || '') === String(item.id);
                        const rowTone = active ? 'bg-teal-50/60' : 'hover:bg-slate-50';
                        const chips = buildStatusChips(item);
                        return (
                          <tr
                            key={item.id}
                            className={cx('cursor-pointer transition', rowTone)}
                            onClick={() => openRecord(item.id)}
                          >
                            <td className="px-3 py-3" onClick={(event) => event.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={selectedIds.includes(Number(item.id))}
                                onChange={() => toggleSelect(item.id)}
                              />
                            </td>
                            <td className="px-3 py-3">
                              <p className="font-semibold text-slate-900">{item.title}</p>
                              <p className="text-xs text-slate-500">{item.slug}</p>
                            </td>
                            <td className="px-3 py-3 text-slate-600">
                              {item.destination_name || item.destination_city || 'Destination pending'}
                            </td>
                            <td className="px-3 py-3 text-slate-600">
                              {item.currency || 'INR'} {item.price_from || item.price_per_person || '0'}
                            </td>
                            <td className="px-3 py-3">
                              <span className={cx('rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em]', getStatusTone(item))}>
                                {item.status || 'draft'}
                              </span>
                            </td>
                            <td className="px-3 py-3 text-xs text-slate-500">
                              {chips.length > 1 ? chips.length - 1 : 0}
                            </td>
                            <td className="px-3 py-3" onClick={(event) => event.stopPropagation()}>
                              <button
                                type="button"
                                onClick={() => copyPackageUrl(item)}
                                className="rounded-full border border-slate-200 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-700 hover:border-teal-400 hover:text-teal-700"
                              >
                                Copy URL
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredItems.map((item) => {
                    const active = String(form.id || '') === String(item.id);
                    const chips = buildStatusChips(item);
                    return (
                      <div
                        key={item.id}
                        className={cx(
                          'rounded-[24px] border px-4 py-4 transition',
                          active ? 'border-teal-500 bg-teal-50/80' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                        )}
                      >
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(Number(item.id))}
                            onChange={() => toggleSelect(item.id)}
                            className="mt-1"
                          />
                          <div
                            role="button"
                            tabIndex={0}
                            onClick={() => openRecord(item.id)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                openRecord(item.id);
                              }
                            }}
                            className="min-w-0 flex-1 cursor-pointer text-left"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="truncate text-base font-semibold text-slate-900">{item.title}</p>
                                <p className="mt-1 text-sm text-slate-500">
                                  {item.destination_name || item.destination_city || 'Destination pending'}
                                </p>
                              </div>
                              <span className={cx('rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em]', getStatusTone(item))}>
                                {item.status || 'draft'}
                              </span>
                            </div>
                            <div className="mt-3 flex flex-wrap items-center gap-2">
                              {chips.map((chip) => (
                                <span
                                  key={`${item.id}-${chip}`}
                                  className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-600"
                                >
                                  {chip}
                                </span>
                              ))}
                            </div>
                            <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
                              <span>
                                {item.currency || 'INR'} {item.price_from || item.price_per_person || '0'}
                              </span>
                              <span>{chips.length > 1 ? chips.length - 1 : 0} badges</span>
                            </div>
                            <div className="mt-3">
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  copyPackageUrl(item);
                                }}
                                className="rounded-full border border-slate-200 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-700 hover:border-teal-400 hover:text-teal-700"
                              >
                                Copy URL
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                No packages found.
              </div>
            )}
          </div>
        </aside>

        <div className="space-y-6">
          <section className="rounded-[32px] border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">Basics</p>
                <h2 className="mt-1 text-lg font-semibold text-slate-950">Package identity</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Core title, destination, pricing, duration, and visibility details.
                </p>
              </div>
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="rounded-full bg-slate-100 px-3 py-1 font-semibold uppercase tracking-[0.18em] text-slate-600">
                  {selectedDestination?.name || 'No destination'}
                </span>
                <span className="rounded-full bg-slate-100 px-3 py-1 font-semibold uppercase tracking-[0.18em] text-slate-600">
                  {form.status}
                </span>
              </div>
            </div>
            <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              <div className="xl:col-span-2">
                <label className={textLabelClass}>Title</label>
                <input value={form.title} onChange={(e) => updateField('title', e.target.value)} className={inputClassName} placeholder="Go Monsoon Villa Retreat" />
                <ErrorText value={errors.title} />
              </div>
              <div>
                <label className={textLabelClass}>Slug</label>
                <input value={form.slug} onChange={(e) => updateField('slug', slugifyText(e.target.value))} className={inputClassName} placeholder="go-monsoon-villa-retreat" />
              </div>
              <div>
                <label className={textLabelClass}>Short title</label>
                <input value={form.shortTitle} onChange={(e) => updateField('shortTitle', e.target.value)} className={inputClassName} placeholder="Monsoon Villa Retreat" />
              </div>
              <div>
                <label className={textLabelClass}>Subtitle</label>
                <input value={form.subtitle} onChange={(e) => updateField('subtitle', e.target.value)} className={inputClassName} placeholder="A slow luxury coastal escape." />
              </div>
              <div>
                <label className={textLabelClass}>Tagline</label>
                <input value={form.tagline} onChange={(e) => updateField('tagline', e.target.value)} className={inputClassName} placeholder="Rainy season, polished perfectly." />
              </div>
              <div>
                <label className={textLabelClass}>Destination</label>
                <select value={form.destinationId} onChange={(e) => updateField('destinationId', e.target.value)} className={inputClassName}>
                  <option value="">Select destination</option>
                  {destinations.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
                <ErrorText value={errors.destinationId} />
              </div>
              <div>
                <label className={textLabelClass}>Days</label>
                <input value={form.durationDays} onChange={(e) => updateField('durationDays', e.target.value)} className={inputClassName} placeholder="5" />
                <ErrorText value={errors.durationDays} />
              </div>
              <div>
                <label className={textLabelClass}>Nights</label>
                <input value={form.durationNights} onChange={(e) => updateField('durationNights', e.target.value)} className={inputClassName} placeholder="4" />
                <ErrorText value={errors.durationNights} />
              </div>
              <div>
                <label className={textLabelClass}>Price from</label>
                <input value={form.priceFrom} onChange={(e) => updateField('priceFrom', e.target.value)} className={inputClassName} placeholder="32000" />
                <ErrorText value={errors.priceFrom} />
              </div>
              <div>
                <label className={textLabelClass}>Original price</label>
                <input value={form.originalPrice} onChange={(e) => updateField('originalPrice', e.target.value)} className={inputClassName} placeholder="38000" />
              </div>
              <div>
                <label className={textLabelClass}>Currency</label>
                <input value={form.currency} onChange={(e) => updateField('currency', e.target.value.toUpperCase())} className={inputClassName} placeholder="INR" />
              </div>
              <div>
                <label className={textLabelClass}>Rating</label>
                <input value={form.rating} onChange={(e) => updateField('rating', e.target.value)} className={inputClassName} placeholder="4.8" />
              </div>
              <div>
                <label className={textLabelClass}>Review count</label>
                <input value={form.reviewCount} onChange={(e) => updateField('reviewCount', e.target.value)} className={inputClassName} placeholder="32" />
              </div>
              <div>
                <label className={textLabelClass}>Min persons</label>
                <input value={form.minPersons} onChange={(e) => updateField('minPersons', e.target.value)} className={inputClassName} placeholder="2" />
              </div>
              <div>
                <label className={textLabelClass}>Max persons</label>
                <input value={form.maxPersons} onChange={(e) => updateField('maxPersons', e.target.value)} className={inputClassName} placeholder="8" />
              </div>
              <div>
                <label className={textLabelClass}>Max guests</label>
                <input value={form.maxGuests} onChange={(e) => updateField('maxGuests', e.target.value)} className={inputClassName} placeholder="8" />
              </div>
              <div>
                <label className={textLabelClass}>Group size</label>
                <input value={form.groupSize} onChange={(e) => updateField('groupSize', e.target.value)} className={inputClassName} placeholder="10" />
              </div>
              <div>
                <label className={textLabelClass}>Difficulty</label>
                <input value={form.difficultyLevel} onChange={(e) => updateField('difficultyLevel', e.target.value)} className={inputClassName} placeholder="Easy" />
              </div>
              <div>
                <label className={textLabelClass}>Availability type</label>
                <select value={form.availabilityType} onChange={(e) => updateField('availabilityType', e.target.value)} className={inputClassName}>
                  <option value="flexible">Flexible</option>
                  <option value="seasonal">Seasonal</option>
                  <option value="fixed">Fixed dates</option>
                  <option value="request_only">Request only</option>
                </select>
              </div>
              <div>
                <label className={textLabelClass}>Availability start</label>
                <input type="date" value={form.availabilityStart} onChange={(e) => updateField('availabilityStart', e.target.value)} className={inputClassName} />
              </div>
              <div>
                <label className={textLabelClass}>Availability end</label>
                <input type="date" value={form.availabilityEnd} onChange={(e) => updateField('availabilityEnd', e.target.value)} className={inputClassName} />
              </div>
              <div>
                <label className={textLabelClass}>Sort order</label>
                <input value={form.sortOrder} onChange={(e) => updateField('sortOrder', e.target.value)} className={inputClassName} placeholder="0" />
              </div>
            </div>
          </section>

          <section className="rounded-[32px] border border-slate-200 bg-white p-6 shadow-sm">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">Content</p>
              <h2 className="mt-1 text-lg font-semibold text-slate-950">Descriptions, themes, and content blocks</h2>
              <p className="mt-1 text-sm text-slate-500">Structured text used on the homepage, listing cards, and the detail page.</p>
            </div>
            <div className="mt-5 grid gap-4 xl:grid-cols-2">
              <div>
                <label className={textLabelClass}>Short description</label>
                <textarea rows={3} value={form.shortDescription} onChange={(e) => updateField('shortDescription', e.target.value)} className={inputClassName} placeholder="A strong summary for cards and search." />
                <ErrorText value={errors.shortDescription} />
              </div>
              <div>
                <label className={textLabelClass}>Overview</label>
                <textarea rows={3} value={form.overview} onChange={(e) => updateField('overview', e.target.value)} className={inputClassName} placeholder="Overview copy for the detail page." />
              </div>
              <div className="xl:col-span-2">
                <label className={textLabelClass}>Full description</label>
                <textarea rows={5} value={form.description} onChange={(e) => updateField('description', e.target.value)} className={inputClassName} placeholder="Longer package story, stay mood, and journey details." />
              </div>
              <div>
                <label className={textLabelClass}>Categories</label>
                <textarea rows={3} value={form.categories} onChange={(e) => updateField('categories', e.target.value)} className={inputClassName} placeholder={'Adventure\nLuxury stays\nWellness'} />
              </div>
              <div>
                <label className={textLabelClass}>Seasonal note</label>
                <textarea rows={3} value={form.seasonalText} onChange={(e) => updateField('seasonalText', e.target.value)} className={inputClassName} placeholder="Best months, weather note, or booking guidance." />
              </div>
              <div>
                <label className={textLabelClass}>Highlights</label>
                <textarea rows={5} value={form.highlights} onChange={(e) => updateField('highlights', e.target.value)} className={inputClassName} placeholder="One per line" />
              </div>
              <div>
                <label className={textLabelClass}>Inclusions</label>
                <textarea rows={5} value={form.inclusions} onChange={(e) => updateField('inclusions', e.target.value)} className={inputClassName} placeholder="One per line" />
              </div>
              <div>
                <label className={textLabelClass}>Exclusions</label>
                <textarea rows={5} value={form.exclusions} onChange={(e) => updateField('exclusions', e.target.value)} className={inputClassName} placeholder="One per line" />
              </div>
            </div>
            <div className="mt-6 rounded-3xl border border-slate-200 bg-slate-50 p-4">
              <p className={textLabelClass}>Themes</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {themes.map((item) => {
                  const active = form.themes.includes(item.id);
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => toggleTheme(item.id)}
                      className={cx(
                        'rounded-full border px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] transition',
                        active ? 'border-teal-500 bg-teal-600 text-white' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                      )}
                    >
                      {item.name}
                    </button>
                  );
                })}
              </div>
            </div>
          </section>

          <section className="rounded-[32px] border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">Media</p>
                <h2 className="mt-1 text-lg font-semibold text-slate-950">Hero, thumbnail, card, and gallery media</h2>
                <p className="mt-1 text-sm text-slate-500">Upload once and assign image roles for cards, hero banners, and the detail gallery.</p>
              </div>
              <label className="inline-flex cursor-pointer items-center justify-center rounded-full bg-slate-900 px-5 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-white">
                Upload images
                <input type="file" multiple className="hidden" onChange={handleUpload} />
              </label>
            </div>
            <div className="mt-5 grid gap-4 lg:grid-cols-3">
              {[
                ['Hero image', 'heroImage', form.heroImage],
                ['Thumbnail image', 'thumbnailImage', form.thumbnailImage],
                ['Card image', 'cardImage', form.cardImage],
              ].map(([label, key, value]) => (
                <div key={key} className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
                  <p className={textLabelClass}>{label}</p>
                  <div className="mt-3 aspect-[4/3] overflow-hidden rounded-2xl border border-slate-200 bg-white">
                    {value ? <img src={value} alt={label} className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-sm text-slate-400">No image assigned</div>}
                  </div>
                  <input value={value} onChange={(e) => updateField(key, e.target.value)} className={inputClassName} placeholder="/uploads/packages/image.jpg" />
                </div>
              ))}
            </div>
            <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {form.images.map((image, index) => (
                <div key={`${image.imageUrl}-${index}`} className="rounded-3xl border border-slate-200 p-3">
                  <div className="aspect-[4/3] overflow-hidden rounded-2xl bg-slate-100">
                    <img src={image.imageUrl} alt={image.altText || 'Package'} className="h-full w-full object-cover" />
                  </div>
                  <input value={image.altText} onChange={(e) => updateImage(index, { altText: e.target.value })} className={inputClassName} placeholder="Alt text" />
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <button type="button" onClick={() => setImageRole('heroImage', image.imageUrl)} className="rounded-full border border-slate-200 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-600">Use as hero</button>
                    <button type="button" onClick={() => setImageRole('thumbnailImage', image.imageUrl)} className="rounded-full border border-slate-200 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-600">Use as thumb</button>
                    <button type="button" onClick={() => setImageRole('cardImage', image.imageUrl)} className="rounded-full border border-slate-200 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-600">Use as card</button>
                    <button type="button" onClick={() => updateImage(index, { isCover: !image.isCover })} className={cx('rounded-full border px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.16em]', image.isCover ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-slate-200 text-slate-600')}>{image.isCover ? 'Cover image' : 'Set cover'}</button>
                  </div>
                  <button type="button" onClick={() => removeImage(index)} className="mt-3 w-full rounded-full border border-rose-200 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-rose-600">Remove image</button>
                </div>
              ))}
              {!form.images.length ? (
                <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500 md:col-span-2 xl:col-span-3">
                  No gallery media uploaded yet.
                </div>
              ) : null}
            </div>
          </section>

          <section className="rounded-[32px] border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">Itinerary</p>
                <h2 className="mt-1 text-lg font-semibold text-slate-950">Day-by-day structure</h2>
                <p className="mt-1 text-sm text-slate-500">Each day supports a title, description, stay info, meals, and an optional image.</p>
              </div>
              <Button variant="outline" onClick={addDay}>
                Add day
              </Button>
            </div>
            <div className="mt-5 space-y-4">
              {form.itinerary.map((item, index) => (
                <div key={`itinerary-${index}`} className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
                  <div className="grid gap-4 md:grid-cols-[120px_1fr] xl:grid-cols-[120px_1fr_180px]">
                    <input value={item.dayNumber} onChange={(e) => updateDay(index, 'dayNumber', e.target.value)} className={inputClassName} placeholder="Day" />
                    <input value={item.dayTitle} onChange={(e) => updateDay(index, 'dayTitle', e.target.value)} className={inputClassName} placeholder="Arrival and welcome" />
                    <input value={item.imageUrl} onChange={(e) => updateDay(index, 'imageUrl', e.target.value)} className={inputClassName} placeholder="Image URL" />
                  </div>
                  <textarea rows={3} value={item.dayDescription} onChange={(e) => updateDay(index, 'dayDescription', e.target.value)} className={inputClassName} placeholder="Describe the day plan." />
                  <div className="mt-3 grid gap-3 md:grid-cols-2">
                    <input value={item.stayInfo} onChange={(e) => updateDay(index, 'stayInfo', e.target.value)} className={inputClassName} placeholder="Stay info" />
                    <input value={item.mealsInfo} onChange={(e) => updateDay(index, 'mealsInfo', e.target.value)} className={inputClassName} placeholder="Meals info" />
                  </div>
                  <div className="mt-3 text-right">
                    <button type="button" onClick={() => removeDay(index)} className="rounded-full border border-rose-200 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-rose-600">Remove day</button>
                  </div>
                </div>
              ))}
              {!form.itinerary.length ? (
                <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
                  No itinerary days added yet.
                </div>
              ) : null}
            </div>
          </section>

          <section className="rounded-[32px] border border-slate-200 bg-white p-6 shadow-sm">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">Publishing</p>
              <h2 className="mt-1 text-lg font-semibold text-slate-950">Badges, visibility, CTA labels, and SEO</h2>
              <p className="mt-1 text-sm text-slate-500">These controls determine homepage visibility, listing visibility, CTA wording, and package metadata.</p>
            </div>
            <div className="mt-5 grid gap-4 xl:grid-cols-2">
              <div className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className={textLabelClass}>Status</label>
                    <select value={form.status} onChange={(e) => updateField('status', e.target.value)} className={inputClassName}>
                      <option value="published">Published</option>
                      <option value="draft">Draft</option>
                      <option value="unpublished">Unpublished</option>
                    </select>
                  </div>
                  <div className="flex items-center rounded-2xl border border-slate-200 px-4 py-3">
                    <label className="flex items-center gap-3 text-sm text-slate-700">
                      <input type="checkbox" checked={form.requestOnly} onChange={(e) => updateField('requestOnly', e.target.checked)} />
                      Request-only package
                    </label>
                  </div>
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  {[
                    ['badgeFeatured', 'Featured badge'],
                    ['badgeTrending', 'Trending badge'],
                    ['badgeTopRated', 'Top rated badge'],
                    ['isFeaturedHome', 'Featured on home'],
                    ['isFeaturedListing', 'Featured on listing'],
                    ['isActive', 'Active'],
                    ['isDraft', 'Mark as draft'],
                  ].map(([key, label]) => (
                    <label key={key} className="flex items-center gap-3 rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700">
                      <input type="checkbox" checked={Boolean(form[key])} onChange={(e) => updateField(key, e.target.checked)} />
                      {label}
                    </label>
                  ))}
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className={textLabelClass}>Enquire button label</label>
                    <input value={form.enquireButtonLabel} onChange={(e) => updateField('enquireButtonLabel', e.target.value)} className={inputClassName} placeholder="Enquire now" />
                    <ErrorText value={errors.enquireButtonLabel} />
                  </div>
                  <div>
                    <label className={textLabelClass}>Detail CTA label</label>
                    <input value={form.detailCtaLabel} onChange={(e) => updateField('detailCtaLabel', e.target.value)} className={inputClassName} placeholder="View details" />
                    <ErrorText value={errors.detailCtaLabel} />
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <label className={textLabelClass}>SEO title</label>
                  <input value={form.seoTitle} onChange={(e) => updateField('seoTitle', e.target.value)} className={inputClassName} placeholder="Luxury package | Homevilas" />
                </div>
                <div>
                  <label className={textLabelClass}>SEO description</label>
                  <textarea rows={3} value={form.seoDescription} onChange={(e) => updateField('seoDescription', e.target.value)} className={inputClassName} placeholder="SEO description for the detail page." />
                </div>
                <div>
                  <label className={textLabelClass}>SEO keywords</label>
                  <input value={form.seoKeywords} onChange={(e) => updateField('seoKeywords', e.target.value)} className={inputClassName} placeholder="go package, monsoon retreat, luxury journey" />
                </div>
                <div>
                  <label className={textLabelClass}>Custom sections JSON</label>
                  <textarea rows={6} value={form.customSections} onChange={(e) => updateField('customSections', e.target.value)} className={`${inputClassName} font-mono text-xs`} placeholder='{"faq":[{"question":"...","answer":"..."}]}' />
                  <ErrorText value={errors.customSections} />
                </div>
              </div>
            </div>
          </section>

          <div className="flex flex-wrap items-center justify-end gap-3">
            {form.id ? (
              <>
                <Button variant="outline" onClick={previewListing}>
                  Preview listing card
                </Button>
                <Button variant="outline" onClick={previewDetail}>
                  Preview detail page
                </Button>
              </>
            ) : null}
            {form.id ? (
              <Button variant="outline" onClick={remove} disabled={saving}>
                Delete package
              </Button>
            ) : null}
            <Button variant="outline" onClick={resetForm}>
              Reset form
            </Button>
            <Button onClick={save} disabled={saving}>
              {saving ? 'Saving...' : 'Save package'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPackages;
